import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reminderWriteSchema, type Inquiry, type Reminder, type ReminderSource } from '@worki/shared';
import { api } from '../lib/api.js';
import { useForm } from '../lib/useForm.js';
import { fromLocalInputValue, toLocalInputValue } from '../lib/format.js';
import { Button, ErrorNotice, Field, Input, Textarea } from './ui.js';
import { Modal } from './Modal.js';
import { useToast } from './Toast.js';
import { CustomerMatchNotice, useCustomerMatch } from './CustomerMatchNotice.js';
import { meetingDraftSchema, scheduleMeeting } from '../lib/meetingCreation.js';
import { cn } from '../lib/cn.js';

/**
 * REM-003 — one reminder editor, reused from the dashboard widget, the
 * reminders screen, the quote page and the customer card. The `source`/`sourceId`
 * pair is what gives a reminder its back-reference to the originating record.
 */
export function ReminderDialog({
  open,
  onClose,
  reminder,
  source = 'manual',
  sourceId = null,
  defaultTitle,
  defaultDueAt,
}: {
  open: boolean;
  onClose: () => void;
  reminder?: Reminder | null;
  source?: ReminderSource;
  sourceId?: string | null;
  defaultTitle?: string;
  /** ISO date used for a new reminder or meeting selected from the calendar. */
  defaultDueAt?: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'reminder' | 'meeting'>('reminder');
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [notificationChannels, setNotificationChannels] = useState<('email' | 'whatsapp')[]>(['email']);
  const [notificationPhone, setNotificationPhone] = useState('');
  const [notifyAt, setNotifyAt] = useState('');
  const [minutesBefore, setMinutesBefore] = useState(30);
  const capabilities = useQuery({ queryKey: ['reminders', 'delivery-capabilities'], queryFn: () => api.get<{ email: boolean; whatsapp: boolean }>('/reminders/delivery-capabilities'), enabled: open });
  const meeting = useForm(meetingDraftSchema, {
    name: '', phone: '', email: '', subject: '', notes: '', meetingAt: defaultDueAt ?? new Date(Date.now() + 86_400_000).toISOString(),
  });
  const match = useCustomerMatch(mode === 'meeting' && !meetingId ? String(meeting.values.phone ?? '') : '', String(meeting.values.name ?? ''));

  const form = useForm(reminderWriteSchema, {
    title: '',
    notes: '',
    dueAt: defaultDueAt ?? new Date(Date.now() + 86_400_000).toISOString(),
    source,
    sourceId,
  });

  const { reset } = form;
  const resetMeeting = meeting.reset;
  useEffect(() => {
    if (!open) return;
    setMode('reminder');
    setMeetingId(null);
    setNotificationChannels(reminder?.notificationChannels ?? ['email']);
    setNotificationPhone(reminder?.notificationPhone ?? '');
    setNotifyAt(reminder?.notifyAt ?? '');
    setMinutesBefore(30);
    resetMeeting({ name: '', phone: '', email: '', subject: defaultTitle ?? '', notes: '', meetingAt: defaultDueAt ?? new Date(Date.now() + 86_400_000).toISOString() });
    reset(
      reminder
        ? {
            title: reminder.title,
            notes: reminder.notes ?? '',
            dueAt: reminder.dueAt,
            source: reminder.source,
            sourceId: reminder.sourceId,
          }
        : {
            title: defaultTitle ?? '',
            notes: '',
            dueAt: defaultDueAt ?? new Date(Date.now() + 86_400_000).toISOString(),
            source,
            sourceId,
          },
    );
  }, [open, reminder, defaultTitle, defaultDueAt, source, sourceId, reset, resetMeeting]);

  const save = useMutation({
    mutationFn: (data: unknown) =>
      reminder
        ? api.patch<Reminder>(`/reminders/${reminder.id}`, data)
        : api.post<Reminder>('/reminders', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['customer'] });
      toast.success(t('common.saved'));
      onClose();
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (mode === 'meeting' && !reminder) {
      if (!meetingId && !match.settle()) return;
      void meeting.submit(async data => {
        const savedMeeting = await scheduleMeeting(data, {
          createdId: meetingId, customerMode: match.mode,
          onCreated: id => {
            setMeetingId(id);
            void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
            void queryClient.invalidateQueries({ queryKey: ['customer'] });
          },
          request: (method, path, body) => api[method]<Inquiry>(path, body),
        });
        await api.post(`/reminders/meeting/${encodeURIComponent(savedMeeting.id)}`, { minutesBefore, notificationChannels, notificationPhone });
        void queryClient.invalidateQueries({ queryKey: ['reminders'] });
        void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
        void queryClient.invalidateQueries({ queryKey: ['calendar'] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        toast.success(t('common.saved'));
        onClose();
      });
      return;
    }
    void form.submit(async (data) => {
      await save.mutateAsync({ ...data, notifyAt: notifyAt || null, notificationChannels, notificationPhone });
    });
  };

  return (
    <Modal
      open={open}
      title={reminder ? t('reminders.edit') : t(mode === 'meeting' ? 'reminders.newMeeting' : 'reminders.new')}
      onClose={form.submitting || meeting.submitting ? () => undefined : onClose}
      footer={
        <>
          <Button variant="ghost" disabled={form.submitting || meeting.submitting} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={form.submitting || meeting.submitting} onClick={handleSubmit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {!reminder && <div role="tablist" aria-label={t('reminders.mode')} className="flex gap-1 rounded-xl bg-muted p-1">
        {(['reminder', 'meeting'] as const).map(option => <button key={option} id={`create-${option}-tab`} type="button" role="tab" tabIndex={mode === option ? 0 : -1} aria-selected={mode === option} aria-controls={`create-${option}-panel`} disabled={form.submitting || meeting.submitting} onClick={() => setMode(option)} onKeyDown={event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 'reminder' : event.key === 'End' ? 'meeting' : option === 'meeting' ? 'reminder' : 'meeting';
          setMode(next);
          event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`#create-${next}-tab`)?.focus();
        }} className={cn('min-h-11 flex-1 rounded-lg px-4 text-sm font-semibold', mode === option ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-accent')}>{t(option === 'meeting' ? 'reminders.meetingTab' : 'reminders.reminderTab')}</button>)}
      </div>}
      <form id="create-reminder-panel" role={!reminder ? 'tabpanel' : undefined} aria-labelledby={!reminder ? 'create-reminder-tab' : undefined} hidden={mode === 'meeting' && !reminder} onSubmit={handleSubmit} className={mode === 'meeting' && !reminder ? 'hidden' : 'stack'} noValidate>
        {form.formError && <ErrorNotice code={form.formError} />}

        <Field
          label={t('reminders.reminderTitle')}
          htmlFor="reminder-title"
          {...(form.errors.title ? { error: t(form.errors.title) } : {})}
        >
          <Input
            id="reminder-title"
            value={String(form.values.title ?? '')}
            invalid={Boolean(form.errors.title)}
            onChange={(event) => form.setValue('title', event.target.value)}
          />
        </Field>

        <Field
          label={t('reminders.dueAt')}
          htmlFor="reminder-due"
          {...(form.errors.dueAt ? { error: t(form.errors.dueAt) } : {})}
        >
          <Input
            id="reminder-due"
            type="datetime-local"
            value={form.values.dueAt ? toLocalInputValue(String(form.values.dueAt)) : ''}
            onChange={(event) => form.setValue('dueAt', event.target.value ? fromLocalInputValue(event.target.value) : '')}
          />
        </Field>

        <Field label={t('reminders.notes')} htmlFor="reminder-notes">
          <Textarea
            id="reminder-notes"
            value={String(form.values.notes ?? '')}
            onChange={(event) => form.setValue('notes', event.target.value)}
          />
        </Field>
      </form>
      {!reminder && <form id="create-meeting-panel" role="tabpanel" aria-labelledby="create-meeting-tab" hidden={mode !== 'meeting'} onSubmit={handleSubmit} className={mode !== 'meeting' ? 'hidden' : 'stack'} noValidate>
        {meeting.formError && <ErrorNotice code={meeting.formError} />}
        {meetingId && meeting.formError && <p role="status" className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm">{t('reminders.meetingCreationIncomplete')} <Link to={`/inquiries?open=${encodeURIComponent(meetingId)}`} className="font-semibold text-primary underline">{t('inquiries.one')}</Link></p>}
        {(['name', 'phone', 'email'] as const).map(key => <Field key={key} label={t(`customer.${key}`)} htmlFor={`meeting-${key}`} {...(meeting.errors[key] ? { error: t(meeting.errors[key]) } : {})}>
          <Input id={`meeting-${key}`} type={key === 'phone' ? 'tel' : key === 'email' ? 'email' : 'text'} dir={key === 'name' ? undefined : 'ltr'} value={String(meeting.values[key] ?? '')} invalid={Boolean(meeting.errors[key])} disabled={Boolean(meetingId) || meeting.submitting} onChange={event => meeting.setValue(key, event.target.value)} />
        </Field>)}
        {!meetingId && <CustomerMatchNotice state={match} name={String(meeting.values.name ?? '')} />}
        <Field label={t('reminders.meetingTitle')} htmlFor="meeting-subject" {...(meeting.errors.subject ? { error: t(meeting.errors.subject) } : {})}>
          <Input id="meeting-subject" value={String(meeting.values.subject ?? '')} invalid={Boolean(meeting.errors.subject)} disabled={meeting.submitting} onChange={event => meeting.setValue('subject', event.target.value)} />
        </Field>
        <Field label={t('inquiries.meetingAt')} htmlFor="meeting-date" {...(meeting.errors.meetingAt ? { error: t(meeting.errors.meetingAt) } : {})}>
          <Input id="meeting-date" type="datetime-local" value={meeting.values.meetingAt ? toLocalInputValue(String(meeting.values.meetingAt)) : ''} invalid={Boolean(meeting.errors.meetingAt)} disabled={meeting.submitting} onChange={event => meeting.setValue('meetingAt', event.target.value ? fromLocalInputValue(event.target.value) : '')} />
        </Field>
        <Field label={t('reminders.notes')} htmlFor="meeting-notes">
          <Textarea id="meeting-notes" value={String(meeting.values.notes ?? '')} disabled={meeting.submitting} onChange={event => meeting.setValue('notes', event.target.value)} />
        </Field>
      </form>}
      <div className="stack border-t border-border pt-4">
        {mode === 'meeting' && !reminder ? <Field label={t('completion.reminderBefore')} htmlFor="meeting-alert-before">
          <select id="meeting-alert-before" className="field" value={minutesBefore} onChange={event => setMinutesBefore(Number(event.target.value))}>
            {[0, 5, 15, 30, 60, 1440].map(minutes => <option key={minutes} value={minutes}>{minutes === 0 ? t('completion.atStart') : t('completion.minutesBefore', { count: minutes })}</option>)}
          </select>
        </Field> : <Field label={t('completion.notifyAt')} htmlFor="reminder-notify-at">
          <Input id="reminder-notify-at" type="datetime-local" value={notifyAt ? toLocalInputValue(notifyAt) : ''} onChange={event => setNotifyAt(event.target.value ? fromLocalInputValue(event.target.value) : '')} />
          <p className="text-xs text-muted-foreground">{t('completion.notifyAtHint')}</p>
        </Field>}
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium">{t('completion.notifyChannels')}</legend>
          <p className="text-xs text-muted-foreground">{t('completion.notifyInApp')}</p>
          {(['email', 'whatsapp'] as const).map(channel => <label key={channel} className="flex min-h-11 items-center gap-2 text-sm">
            <input type="checkbox" checked={notificationChannels.includes(channel)} disabled={channel === 'whatsapp' && !capabilities.data?.whatsapp && !notificationChannels.includes(channel)} onChange={event => setNotificationChannels(current => event.target.checked ? [...current, channel] : current.filter(value => value !== channel))} />
            {t(channel === 'email' ? 'completion.notifyEmail' : 'completion.notifyWhatsapp')}
            {channel === 'whatsapp' && !capabilities.data?.whatsapp && <span className="text-xs text-muted-foreground">{t('completion.whatsappUnavailable')}</span>}
          </label>)}
        </fieldset>
        {notificationChannels.includes('whatsapp') && <Field label={t('completion.notifyPhone')} htmlFor="reminder-notify-phone"><Input id="reminder-notify-phone" type="tel" dir="ltr" value={notificationPhone} onChange={event => setNotificationPhone(event.target.value)} /></Field>}
      </div>
    </Modal>
  );
}
